import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import {
  ArrowLeft, ShieldCheck, MessageCircle, MapPin, Languages, Wallet, Phone,
  Globe, Instagram, Car, Navigation as NavIcon, Share2, Heart, QrCode, Copy,
  Check, Loader2, Footprints, Crown, Briefcase, Cigarette, Camera, User,
} from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { api, getMe } from "../lib/api";
import { Stars } from "../components/PlaceCard";
import { BottomNav } from "../components/BottomNav";
import { ReviewsSection } from "../components/Reviews";
import LogSpendModal from "../components/LogSpendModal";
import SpendHistoryStrip from "../components/SpendHistoryStrip";
import GuestGate from "../components/GuestGate";
import * as guestStore from "../lib/guestStorage";
import { useI18n } from "../lib/i18n";
import { formatPhoneWithFlag } from "../lib/formatPhone";
import { useGps } from "../lib/useGps";
import { haversineKm, fmtDistanceWalk } from "../lib/geo";
import DriverQRModal from "../components/DriverQRModal";
import { sendRideRequest, sendVendorContact } from "../lib/sendRideRequest";
import { shareViaWhatsApp } from "../lib/openWhatsApp";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { OwnerDashboardToggle, OwnerEditButton, BackToHomeFooter } from "../components/OwnerToolbar";
import EditSheet from "../components/EditSheet";

const cleanPhone = (s) => (s || "").replace(/\D/g, "");

const VENDOR_CATEGORY_LABEL = {
  driver: "Driver",
  tour_guide: "Tour Guide",
  cigar_roller: "Cigar Roller",
  artist: "Artist",
  other: "Vendor",
};

const VENDOR_CATEGORY_ICON = {
  driver: Car,
  tour_guide: MapPin,
  cigar_roller: Cigarette,
  artist: Camera,
  other: User,
};

// iter215 — Renders "Category · Subcategory" using the current app
// language. Reads from the vendor's `category_slug` + `subcategory_slug`
// and the public vendor_categories / transport_subcategories catalogues.
function VendorCategoryLabel({ vendor }) {
  const [cats, setCats] = useState([]);
  const [subs, setSubs] = useState([]);
  const lang = (() => {
    try { return (localStorage.getItem("habano.lang") || "en").toLowerCase(); }
    catch { return "en"; }
  })();
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-categories`)
      .then((r) => r.json()).then(setCats).catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/transport-subcategories`)
      .then((r) => r.json()).then(setSubs).catch(() => {});
  }, []);
  if (!vendor?.category_slug) return null;
  const cat = cats.find((c) => c.slug === vendor.category_slug);
  const sub = vendor.subcategory_slug ? subs.find((s) => s.slug === vendor.subcategory_slug) : null;
  const catName = cat ? (lang === "es" ? cat.name_es : cat.name_en) : vendor.category_slug;
  const subName = sub ? (lang === "es" ? sub.name_es : sub.name_en) : null;
  return (
    <span
      className="pill border border-gold-soft text-gold bg-[rgba(232,184,75,0.08)]"
      data-testid="vendor-category-label"
    >
      {catName}{subName ? ` · ${subName}` : ""}
    </span>
  );
}


export default function VendorPublic() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [v, setV] = useState(null);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [saved, setSaved] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [spendOpen, setSpendOpen] = useState(false);
  const [spendRefresh, setSpendRefresh] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [guestGate, setGuestGate] = useState({ open: false, action: null });
  const [checkingIn, setCheckingIn] = useState(false);
  const [justCheckedIn, setJustCheckedIn] = useState(false);
  const [vendorDirOpen, setVendorDirOpen] = useState(false);
  const { coords: gpsCoords, state: gpsState, requestGps } = useGps();
  const { lang, t: tI18n } = useI18n();

  useEffect(() => {
    // iter216 — Merge in hero_bg_url as the fallback banner when the
    // vendor hasn't uploaded a custom one.
    Promise.all([
      api.get(`/vendors/${id}`).then((r) => r.data),
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public-settings`).then((r) => r.json()).catch(() => ({})),
    ]).then(([vendorData, settings]) => {
      if (!vendorData.background_image && settings.hero_bg_url) {
        vendorData.background_image = settings.hero_bg_url;
      }
      setV(vendorData);
    }).catch((e) => setError(e.response?.status === 404 ? "Vendor not available" : e.message));
    getMe().then((me) => {
      setUser(me);
      if (me) {
        api.get("/favorites").then(({ data }) => {
          setSaved((data || []).some((f) => f.target_id === id && f.target_type === "vendor"));
        }).catch(() => {});
      } else {
        setSaved(guestStore.isSaved(id, "vendor"));
      }
    });
    // QR scan tracking — when arrived with ?qr=1 we log a qr_scan interaction.
    if (params.get("qr") === "1") {
      api.post(`/vendors/${id}/qr-scan`, {}).catch(() => {});
    }
  }, [id, params]);

  const toggleSave = async () => {
    if (!user) {
      const next = guestStore.toggleSaved(id, "vendor");
      setSaved(next);
      toast.success(next ? "Saved" : "Removed");
      return;
    }
    const prev = saved;
    setSaved(!prev);
    try {
      await api.post("/favorites/toggle", { target_id: id, target_type: "vendor" });
    } catch {
      setSaved(prev);
      toast.error("Couldn't save");
    }
  };

  const doCheckIn = async () => {
    if (checkingIn || justCheckedIn) return;
    setCheckingIn(true);
    try {
      if (!user) {
        const r = guestStore.appendCheckin({ target_type: "vendor", target_id: id, target_name: v?.full_name || null });
        if (r.duplicate) {
          setJustCheckedIn(true);
          toast.info(lang === "es" ? "Ya registrado recientemente" : "Already checked in recently");
        } else if (r.ok) {
          setJustCheckedIn(true);
          toast.success(lang === "es" ? `Registro con ${v?.full_name}` : `Checked in with ${v?.full_name}`);
          setTimeout(() => setJustCheckedIn(false), 30 * 60 * 1000);
        }
        return;
      }
      const { data } = await api.post(`/vendors/${id}/check-in`, {});
      if (data?.ok) {
        setJustCheckedIn(true);
        toast.success(data.message || `Checked in with ${v?.full_name}`);
        setTimeout(() => setJustCheckedIn(false), 30 * 60 * 1000);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Check-in failed");
    } finally { setCheckingIn(false); }
  };

  const contactWA = () => {
    const phone = cleanPhone(v?.whatsapp);
    if (!phone) { toast.error("Vendor hasn't shared a WhatsApp number"); return; }
    api.post("/vendors/whatsapp-contact", { vendor_id: v.id }).catch(() => {});
    api.post("/whatsapp/interaction", { target_id: v.id, target_type: "vendor", kind: "contact_vendor" }).catch(() => {});
    // Route through WhatsAppActionSheet (Type 3 vendor) so guests are gated naturally.
    sendVendorContact({ name: v.full_name, whatsapp: v.whatsapp, vendor_id: v.id }, { source: "vendor-public-contact" });
  };

  if (error) {
    return (
      <main className="min-h-screen pt-24 px-6 bg-background" data-testid="vendor-not-found">
        <div className="max-w-md mx-auto text-center lux-card p-8" style={{ background: "#171717" }}>
          <span className="mx-auto w-12 h-12 grid place-items-center rounded-full border border-gold-soft text-amber-300">
            <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
          </span>
          <div className="font-display text-2xl mt-4 text-foreground">{error}</div>
          <div className="text-sm text-foreground/55 mt-2">This vendor may have paused activity. Try again later.</div>
          <Link to="/" className="mt-6 btn-outline py-2 px-4 text-xs inline-flex" style={{ minHeight: 44 }}>Back home</Link>
        </div>
        <BottomNav />
      </main>
    );
  }
  if (!v) return null;

  const categoryKey = (v.category || "other").toLowerCase();
  const CategoryIcon = VENDOR_CATEGORY_ICON[categoryKey] || User;
  const hasLocation = v.location_visible && (v.lat != null && v.lng != null);
  const hasAddress = v.location_visible && !!v.address;
  const showRide = hasLocation || hasAddress;
  const showNavigate = hasLocation || hasAddress;

  return (
    <>
    <main className="min-h-screen pb-32 bg-background" data-testid="vendor-public">
      <VendorPhotoGallery
        vendor={v}
        CategoryIcon={CategoryIcon}
        saved={saved}
        onToggleSave={toggleSave}
      />

      {/* Title block */}
      <section className="mt-6 relative z-10 px-6">
        {/* iter216 — Owner-only DASHBOARD toggle. Guests + regular users
            do not see this; when the current user is the vendor's owner
            we render the Business/User pill at the very top of the
            title block. */}
        <OwnerDashboardToggle
          isOwner={!!(user && v && (user.user_id === v.owner_user_id || user.user_id === v.user_id))}
          ownerDashboardPath="/vendor"
        />
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {v.verified && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest text-gold border border-gold-strong bg-gold/10" data-testid="vendor-verified">
                <ShieldCheck className="w-3 h-3" strokeWidth={2}/> Verified
              </span>
            )}
            <VendorCategoryLabel vendor={v}/>
          {v.service_type === "driver" && (() => {
            const beat = v.location_updated_at ? new Date(v.location_updated_at).getTime() : 0;
            const fresh = beat && (Date.now() - beat) < 5 * 60 * 1000;
            const isLive = fresh && v.available !== false;
            return (
              <span
                data-testid="vendor-live-availability"
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest"
                style={{
                  color: isLive ? "#4AE880" : "#9A9490",
                  border: `1px solid ${isLive ? "rgba(74,232,128,0.5)" : "rgba(154,148,144,0.35)"}`,
                  background: isLive ? "rgba(74,232,128,0.08)" : "rgba(154,148,144,0.05)",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: isLive ? "#4AE880" : "#9A9490",
                    boxShadow: isLive ? "0 0 0 3px rgba(74,232,128,0.28)" : "none",
                  }}
                />
                {isLive ? "Live now" : "Offline"}
              </span>
            );
          })()}
          </div>
          <OwnerEditButton
            isOwner={!!(user && v && (user.user_id === v.owner_user_id || user.user_id === v.user_id))}
            onClick={() => setEditOpen(true)}
          />
        </div>
        <h1 className="font-display text-5xl sm:text-6xl text-foreground leading-[0.95] tracking-tight" data-testid="vendor-name">
          {v.full_name}
        </h1>

        {v.whatsapp && (
          <button
            type="button"
            data-testid="vendor-whatsapp-chip"
            onClick={() => {
              api.post("/whatsapp/interaction", { target_id: v.id, target_type: "vendor", kind: "contact_vendor" }).catch(() => {});
              sendVendorContact({ name: v.full_name, whatsapp: v.whatsapp, vendor_id: v.id }, { source: "vendor-public-header-chip" });
            }}
            className="inline-flex items-center gap-2 mt-3 transition-colors hover:brightness-110"
            style={{
              minHeight: 32,
              padding: "6px 12px",
              background: "rgba(37,211,102,0.10)",
              border: "1px solid rgba(37,211,102,0.35)",
              borderRadius: 999,
              color: "#25d366",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
            <span>WhatsApp</span>
          </button>
        )}

        {/* Distance & walking time */}
        {hasLocation && gpsCoords && (() => {
          const dist = fmtDistanceWalk(haversineKm(gpsCoords, { lat: v.lat, lng: v.lng }));
          if (!dist) return null;
          return (
            <div
              className="mt-3 inline-flex items-center gap-1.5"
              style={{ color: "#B8B2A7", fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}
              data-testid="vendor-distance"
            >
              <Footprints className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>{dist}</span>
            </div>
          );
        })()}
        {hasLocation && !gpsCoords && (gpsState === "denied" || gpsState === "idle") && (
          <button
            type="button"
            onClick={requestGps}
            data-testid="vendor-enable-location"
            className="mt-3 inline-flex items-center gap-1 hover:opacity-80 transition-opacity"
            style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 11 }}
          >
            <MapPin className="w-3 h-3" strokeWidth={1.5} /> Enable location
          </button>
        )}

        {/* Ratings */}
        {v.ratings_visible && v.ratings_average != null && (
          <div className="mt-4 flex items-center gap-2" data-testid="vendor-rating">
            <Stars value={v.ratings_average} size={14} />
            <span className="text-sm text-foreground/75 font-mono">{v.ratings_average} ({v.approved_review_count})</span>
          </div>
        )}
        {!v.ratings_visible && (
          <div className="mt-3 text-xs text-foreground/45 font-mono" data-testid="vendor-rating-locked">
            Need 3+ approved reviews to show ratings · {v.approved_review_count || 0}/3
          </div>
        )}

        {/* Bio */}
        {v.bio && (
          <p className="mt-7 text-foreground/75 leading-[1.7] font-display-light text-lg max-w-prose" data-testid="vendor-bio">
            {v.bio}
          </p>
        )}

        {/* Languages */}
        {Array.isArray(v.languages) && v.languages.length > 0 && (
          <div className="mt-5 flex items-start gap-3" data-testid="vendor-languages">
            <Languages className="w-4 h-4 text-gold mt-0.5 shrink-0" strokeWidth={1.5} />
            <div className="text-sm text-foreground/85">{v.languages.join(" · ")}</div>
          </div>
        )}

        <div className="rule-soft my-10" />

        {/* iter104 — Vendor prominent action layout (precedes the legacy action grid). */}
        <div className="space-y-3 mb-3" data-testid="vendor-prominent-actions">
          {v.service_type && v.service_type !== "driver" && (
            <Link
              to={`/v/${v.id}/message`}
              data-testid="vendor-action-message-prominent"
              className="w-full inline-flex items-center justify-center gap-2 hover:opacity-95 transition-opacity"
              style={{
                height: 52,
                background: "transparent",
                borderRadius: 12,
                color: "#E8B84B",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 16, fontWeight: 700,
                border: "1.5px solid #E8B84B",
                textDecoration: "none",
              }}
            >
              <MessageCircle style={{ width: 18, height: 18 }} strokeWidth={2} />
              Send a Request
            </Link>
          )}
          {v.whatsapp && (
            <button
              type="button"
              data-testid="vendor-action-contact-prominent"
              onClick={contactWA}
              className="w-full inline-flex items-center justify-center gap-2 hover:opacity-95 transition-opacity"
              style={{
                height: 52,
                background: "linear-gradient(180deg, #F5CC55, #E8B84B)",
                borderRadius: 12,
                color: "#0B0B0B",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 16, fontWeight: 700,
                border: "none",
                boxShadow: "0 4px 16px rgba(232,184,75,0.35)",
              }}
            >
              <MessageCircle style={{ width: 18, height: 18 }} strokeWidth={2} />
              {tI18n("contact_vendor_btn")}
            </button>
          )}
          {(showRide || (v.lat != null && v.lng != null)) && (
            <div className="grid grid-cols-2 gap-2">
              {showRide && (
                <button
                  type="button"
                  data-testid="vendor-action-ride-prominent"
                  onClick={() => sendRideRequest(
                    { name: v.full_name, address: v.address, lat: v.lat, lng: v.lng },
                    { source: "vendor-profile-ride" },
                  )}
                  className="inline-flex items-center justify-center gap-1.5 transition-colors hover:bg-white/[0.04]"
                  style={{
                    height: 44, borderRadius: 10, padding: "0 10px",
                    background: "transparent", color: "#E8B84B",
                    fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600,
                    border: "1.5px solid rgba(232,184,75,0.55)",
                  }}
                >
                  <Car className="w-4 h-4" strokeWidth={2} /> {tI18n("ride_request_driver")}
                </button>
              )}
              {v.lat != null && v.lng != null && (
                <button
                  type="button"
                  data-testid="vendor-action-view-directions"
                  onClick={() => setVendorDirOpen(true)}
                  className="inline-flex items-center justify-center gap-1.5 transition-colors hover:bg-white/[0.04]"
                  style={{
                    height: 44, borderRadius: 10, padding: "0 10px",
                    background: "transparent", color: "#E8B84B",
                    fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600,
                    border: "1.5px solid rgba(232,184,75,0.55)",
                  }}
                >
                  <QrCode className="w-4 h-4" strokeWidth={2} /> {tI18n("ride_view_directions")}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Action grid — only configured fields are rendered */}
        <div className="grid grid-cols-2 gap-3" data-testid="vendor-actions">
          {/* iter216 — Vendor "Save" button. Same action as the heart:
              writes to habano.saved with target_type="vendor" and mirrors
              via /favorites/toggle when the user is logged in. Business
              profiles retain "Check In" wording (unchanged). */}
          <button
            data-testid="vendor-action-save"
            onClick={toggleSave}
            className={
              "text-xs col-span-2 justify-center " +
              (saved ? "btn-outline border-gold-strong text-gold" : "btn-gold")
            }
            style={{ minHeight: 48 }}
          >
            <Heart className="w-4 h-4" fill={saved ? "currentColor" : "none"} strokeWidth={1.5} />
            {saved
              ? (lang === "es" ? "Guardado" : "Saved")
              : (lang === "es" ? "Guardar" : "Save")}
          </button>

          {(v.whatsapp || v.phone) && (
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="vendor-contact-numbers">
              {v.whatsapp && (
                <div className="text-foreground/80" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>
                  <MessageCircle className="inline w-3.5 h-3.5 mr-1 text-[#25D366]" strokeWidth={1.5}/>
                  <span data-testid="vendor-whatsapp-number">{formatPhoneWithFlag(v.whatsapp)}</span>
                </div>
              )}
              {v.phone && v.phone !== v.whatsapp && (
                <div className="text-foreground/80" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>
                  <Phone className="inline w-3.5 h-3.5 mr-1 text-gold" strokeWidth={1.5}/>
                  <span data-testid="vendor-phone-number">{formatPhoneWithFlag(v.phone)}</span>
                </div>
              )}
            </div>
          )}

          {v.whatsapp && (
            <button
              data-testid="vendor-action-whatsapp"
              onClick={contactWA}
              className="px-4 py-3 bg-[#25D366] text-white text-sm inline-flex items-center justify-center gap-2 rounded-md hover:opacity-90"
              style={{ minHeight: 48 }}
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.5}/> WhatsApp
            </button>
          )}
          {v.phone && (
            <a
              data-testid="vendor-action-call"
              href={`tel:${v.phone}`}
              className="btn-ghost border border-gold-soft text-xs justify-center"
              style={{ minHeight: 48 }}
            >
              <Phone className="w-4 h-4" strokeWidth={1.5} /> Call
            </a>
          )}
          {showRide && (
            <button
              data-testid="vendor-action-ride"
              type="button"
              onClick={async () => {
                if (!gpsCoords && gpsState !== "granted") await requestGps("ride");
                const pickupLine = gpsCoords ? `Pickup: ${gpsCoords.lat.toFixed(5)},${gpsCoords.lng.toFixed(5)}\n` : "";
                const msg = `${pickupLine}Ride to ${v.full_name}${v.address ? `, ${v.address}` : ""}`;
                // iter139 — popup-blocker-safe dispatch.
                shareViaWhatsApp(msg);
              }}
              className="btn-outline text-xs justify-center"
              style={{ minHeight: 48 }}
            >
              <Car className="w-4 h-4" strokeWidth={1.5} /> Request Ride
            </button>
          )}
          {showNavigate && (
            <button
              data-testid="vendor-action-navigate"
              type="button"
              onClick={async () => {
                if (!gpsCoords && gpsState !== "granted") await requestGps("navigate");
                const dest = v.lat != null && v.lng != null
                  ? `https://www.google.com/maps/dir/?api=1&destination=${v.lat},${v.lng}`
                  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(v.address || v.full_name)}`;
                window.open(dest, "_blank", "noopener");
              }}
              className="btn-ghost border border-gold-soft text-xs justify-center"
              style={{ minHeight: 48 }}
            >
              <NavIcon className="w-4 h-4" strokeWidth={1.5} /> Navigate
            </button>
          )}
          {v.website && (
            <a
              data-testid="vendor-action-website"
              href={v.website.startsWith("http") ? v.website : `https://${v.website}`}
              target="_blank" rel="noopener noreferrer"
              className="btn-ghost border border-gold-soft text-xs justify-center"
              style={{ minHeight: 48 }}
            >
              <Globe className="w-4 h-4" strokeWidth={1.5} /> Website
            </a>
          )}
          {v.instagram && (
            <a
              data-testid="vendor-action-instagram"
              href={`https://instagram.com/${(v.instagram || "").replace(/^@/, "")}`}
              target="_blank" rel="noopener noreferrer"
              className="btn-ghost border border-gold-soft text-xs justify-center"
              style={{ minHeight: 48 }}
            >
              <Instagram className="w-4 h-4" strokeWidth={1.5} /> Instagram
            </a>
          )}
          <button
            data-testid="vendor-action-share"
            onClick={() => setShareOpen(true)}
            className="btn-ghost border border-gold-soft text-xs justify-center"
            style={{ minHeight: 48 }}
          >
            <Share2 className="w-4 h-4" strokeWidth={1.5} /> Share
          </button>
          {user && (
            <button
              data-testid="vendor-action-log-spend"
              onClick={() => setSpendOpen(true)}
              className="btn-ghost border border-gold-soft text-xs justify-center"
              style={{ minHeight: 48 }}
            >
              <Wallet className="w-4 h-4" strokeWidth={1.5} /> Log Spend
            </button>
          )}
        </div>

        <SpendHistoryStrip
          user={user}
          targetType="vendor"
          targetId={v.id}
          refreshKey={spendRefresh}
          onTap={() => setSpendOpen(true)}
        />

        {/* Detail rows */}
        {(v.areas?.length || hasAddress) && (
          <>
            <div className="rule my-8" />
            <dl className="space-y-4 text-sm" data-testid="vendor-details">
              {hasAddress && (
                <DetailRow icon={MapPin} label="Address" value={v.address} />
              )}
              {v.areas?.length > 0 && (
                <DetailRow icon={MapPin} label="Areas served" value={v.areas.join(" · ")} />
              )}
            </dl>
          </>
        )}
      </section>

      <ReviewsSection
        targetId={v.id}
        targetType="vendor"
        targetName={v.full_name}
        canReview={v.can_review}
        ratingsVisible={v.ratings_visible}
        approvedCount={v.approved_review_count}
      />

      <VendorShareDialog open={shareOpen} onOpenChange={setShareOpen} vendor={v} />
      <LogSpendModal
        open={spendOpen}
        onOpenChange={setSpendOpen}
        targetType="vendor"
        targetId={v.id}
        targetName={v.full_name}
        onSaved={() => setSpendRefresh((n) => n + 1)}
      />
      <GuestGate
        open={guestGate.open}
        onOpenChange={(o) => setGuestGate({ open: o, action: o ? guestGate.action : null })}
        action={guestGate.action}
      />
      <BottomNav />
      <DriverQRModal
        open={vendorDirOpen}
        onClose={() => setVendorDirOpen(false)}
        business={{ name: v?.full_name, address: v?.address, lat: v?.lat, lng: v?.lng, category: v?.category }}
        testidPrefix="vendor-directions"
      />
      <BackToHomeFooter />
    </main>
    <EditSheet
      open={editOpen}
      onClose={() => setEditOpen(false)}
      kind="vendor"
      profile={v}
      onSaved={(patched) => setV((prev) => ({ ...prev, ...patched }))}
    />
    </>
  );
}

/* --------------------------------------------------------------------- */
/* Photo gallery — mirrors PlaceProfile PhotoGallery for full parity.   */
/* --------------------------------------------------------------------- */
function VendorPhotoGallery({ vendor, CategoryIcon, saved, onToggleSave }) {
  const slides = useMemo(() => {
    const abs = (s) => (s && s.startsWith?.("http") ? s : `${process.env.REACT_APP_BACKEND_URL}${s || ""}`);
    const out = [];
    // photos array first
    (vendor.photos || []).forEach((p) => {
      const src = p?.full || p?.medium || p?.thumbnail || p?.url || (typeof p === "string" ? p : null);
      if (src) out.push({ src: abs(src) });
    });
    // legacy selfie_photo as a fallback hero
    if (!out.length && vendor.selfie_photo) {
      const sp = vendor.selfie_photo;
      const src = sp?.medium || sp?.large || sp?.full || sp?.thumbnail || (typeof sp === "string" ? sp : null);
      if (src) out.push({ src: abs(src) });
    }
    return out;
  }, [vendor]);

  const [emblaRef, embla] = useEmblaCarousel({ loop: false });
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setActive(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    onSelect();
    return () => embla.off("select", onSelect);
  }, [embla]);

  const hasMultiple = slides.length > 1;
  const isEmpty = slides.length === 0;

  return (
    <div className="place-profile-hero-wrap relative bg-[hsl(var(--surface-elevated))] overflow-hidden" data-testid="vendor-hero-gallery">
      {isEmpty ? (
        <div className="place-profile-hero w-full grid place-items-center bg-[#0a0a0a]" data-testid="vendor-hero-placeholder">
          <CategoryIcon className="w-12 h-12 text-gold" strokeWidth={1.25} />
        </div>
      ) : !hasMultiple ? (
        <img
          src={slides[0].src}
          alt={vendor.full_name}
          loading="lazy"
          className="place-profile-hero"
          data-testid="vendor-hero-photo"
        />
      ) : (
        <div ref={emblaRef} className="overflow-hidden h-full">
          <div className="flex h-full">
            {slides.map((s, i) => (
              <div key={i} className="flex-[0_0_100%] min-w-0 h-full" data-testid={`vendor-hero-slide-${i}`}>
                {Math.abs(i - active) <= 1 ? (
                  <img src={s.src} alt={`${vendor.full_name} ${i + 1}`} loading="lazy" className="place-profile-hero" />
                ) : <div className="w-full h-full bg-[#0a0a0a]" />}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent pointer-events-none" />

      <Link
        to="/"
        data-testid="vendor-back"
        className="absolute top-5 left-5 w-11 h-11 rounded-full grid place-items-center glass-dark border border-gold-soft text-foreground hover:text-gold transition-colors duration-300 z-10"
        aria-label="Back"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
      </Link>
      <button
        data-testid="vendor-save"
        onClick={onToggleSave}
        aria-label={saved ? "Saved" : "Save"}
        className={`absolute top-5 right-5 w-11 h-11 rounded-full grid place-items-center border transition-all duration-300 z-10 ${
          saved
            ? "bg-gold text-[hsl(var(--primary-foreground))] border-transparent glow-gold-strong"
            : "glass-dark border-gold-soft text-foreground hover:text-gold"
        }`}
      >
        <Heart className="w-4 h-4" fill={saved ? "currentColor" : "none"} strokeWidth={1.5} />
      </button>

      {hasMultiple && (
        <>
          <div
            className="absolute top-5 right-20 px-2.5 py-1 rounded-full text-[11px] font-mono text-white tracking-widest pointer-events-none z-10"
            style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
            data-testid="vendor-hero-counter"
          >
            {active + 1} / {slides.length}
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => embla?.scrollTo(i)}
                aria-label={`Photo ${i + 1}`}
                data-testid={`vendor-hero-dot-${i}`}
                className={`h-1 rounded-full transition-all duration-500 ${i === active ? "bg-gold w-8" : "bg-foreground/40 w-1.5"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Share dialog — QR + WhatsApp only.                                    */
/* --------------------------------------------------------------------- */
function VendorShareDialog({ open, onOpenChange, vendor }) {
  const link = typeof window !== "undefined" ? `${window.location.origin}/vendors/${vendor.id}` : "";
  const message = `Hey I found this vendor on Habano App — ${vendor.full_name}. Check them out: ${link}`;
  const [mode, setMode] = useState("share"); // "share" | "qr"
  const wa = () => {
    // iter139 — popup-blocker-safe dispatch.
    shareViaWhatsApp(message);
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setMode("share"); }}>
      <DialogContent data-testid="vendor-share-dialog" style={{ background: "#171717" }}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-foreground">
            {mode === "qr" ? "Vendor QR" : `Share ${vendor.full_name}`}
          </DialogTitle>
          <DialogDescription>
            {mode === "qr"
              ? "Scan with any phone camera to open this vendor's profile."
              : "Pass this profile to a friend."}
          </DialogDescription>
        </DialogHeader>
        {mode === "qr" ? (
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="p-4 bg-white rounded-md">
              <QRCodeSVG value={link} size={220} level="M" />
            </div>
            <div className="text-xs font-mono break-all text-center text-foreground/55 max-w-xs" data-testid="vendor-share-qr-url">
              {link}
            </div>
            <button
              onClick={copy}
              data-testid="vendor-share-copy"
              className="btn-outline py-2 px-4 text-xs"
              style={{ minHeight: 44 }}
            >
              <Copy className="w-4 h-4" strokeWidth={1.5} /> Copy link
            </button>
            <button
              onClick={() => setMode("share")}
              className="text-xs text-foreground/55"
              style={{ minHeight: 44 }}
            >
              ← Back
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <button
              data-testid="vendor-share-wa"
              onClick={wa}
              className="px-4 py-3 bg-[#25D366] text-white text-sm inline-flex items-center justify-center gap-2 rounded-md"
              style={{ minHeight: 48 }}
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Share via WhatsApp
            </button>
            <button
              data-testid="vendor-share-qr-open"
              onClick={() => setMode("qr")}
              className="btn-outline text-xs"
              style={{ minHeight: 48 }}
            >
              <QrCode className="w-4 h-4" strokeWidth={1.5} /> Show QR Code
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-gold mt-0.5 shrink-0" strokeWidth={1.5} />
      <div>
        <div className="eyebrow mb-0.5">{label}</div>
        <div className="text-foreground/85">{value}</div>
      </div>
    </div>
  );
}
